/**
 * 동기화 병합 로직 — 결정 #20(하이브리드 + 매니페스트 diff)의 플랫폼 중립 코어.
 *
 * 전송(SyncBackend)과 달리 "무엇을 주고받고, 충돌을 어떻게 판정하는가"는 순수
 * 함수다. 여기에 그 결정 로직만 담아 단위 테스트로 고정한다(전송/네트워크는 web의
 * SyncEngine 구현이 담당).
 *
 * 핵심 원칙(결정 #20): **절대 침묵의 데이터 손실 없음.** 자동 병합이 가능한
 * 경우(한쪽만 변경)만 자동으로 처리하고, 양쪽이 공통 베이스에서 갈라진 "진짜
 * 충돌"만 사용자에게 올린다.
 */
import type { SyncManifest, SyncRecord } from "../ports/sync.js";

/** 한 레코드에 대한 병합 결정. */
export type MergeDecision =
  | "equal" // 내용 동일(hash 같음) → 아무것도 안 함
  | "use-local" // 로컬만 변경됨 → 로컬을 원격에 push
  | "use-remote" // 원격만 변경됨 → 원격을 로컬에 적용
  | "conflict"; // 양쪽이 베이스에서 갈라짐 → 사용자 판단 필요

/** 매니페스트 한 항목(id별 버전 상태). */
type ManifestEntry = SyncManifest[string];

/**
 * 두 레코드(또는 매니페스트 항목)의 병합을 결정한다(결정 #20).
 *
 * @param local  로컬 상태(없으면 undefined).
 * @param remote 원격 상태(없으면 undefined).
 * @param base   직전 동기화 시점의 공통 상태(없으면 첫 동기화).
 *
 * 규칙:
 *  - 양쪽 hash가 같으면 equal(rev가 달라도 내용이 같으면 충돌 아님).
 *  - 한쪽만 존재하면 그쪽을 채택(append 또는 tombstone 전파).
 *  - base가 있으면 3-way: 양쪽 모두 base에서 바뀌면 conflict, 한쪽만 바뀌면 그쪽.
 *  - base가 없고(첫 동기화) 양쪽이 다르면 conflict(임의로 버리지 않음).
 */
export function decideMerge(
  local: ManifestEntry | undefined,
  remote: ManifestEntry | undefined,
  base?: ManifestEntry | undefined,
): MergeDecision {
  // 한쪽만 존재.
  if (local && !remote) {
    // base가 있었는데 원격에서 사라졌다면 원격이 삭제(tombstone)했을 수 있다.
    // 하지만 tombstone은 별도 레코드로 표현되므로(deleted=true), "없음"은 보통
    // 로컬이 새로 만든 것 → push.
    return "use-local";
  }
  if (!local && remote) {
    return "use-remote";
  }
  if (!local && !remote) {
    return "equal"; // 둘 다 없음 — 할 일 없음
  }

  // 여기부터 둘 다 존재.
  const l = local as ManifestEntry;
  const r = remote as ManifestEntry;

  if (l.hash === r.hash) return "equal";

  if (base) {
    const localChanged = l.hash !== base.hash;
    const remoteChanged = r.hash !== base.hash;
    if (localChanged && remoteChanged) return "conflict";
    if (localChanged) return "use-local";
    if (remoteChanged) return "use-remote";
    // 둘 다 base와 같은데 서로 다르다? 모순 — LWW로 폴백.
    return lww(l, r);
  }

  // 공통 베이스가 없음(첫 동기화): 데이터 손실 방지를 위해 충돌로 올린다.
  return "conflict";
}

/** rev → updatedAt 순의 Last-Write-Wins 폴백(결정 #20). */
function lww(l: ManifestEntry, r: ManifestEntry): MergeDecision {
  if (l.rev !== r.rev) return l.rev > r.rev ? "use-local" : "use-remote";
  // rev 동률이면 매니페스트엔 시각이 없으므로 안전하게 충돌로.
  return "conflict";
}

/** 충돌 시 엔진이 제안할 기본 해법(결정 #20: 사용자에게 표시 전 제안값). */
export function suggestResolution(
  local: SyncRecord,
  remote: SyncRecord,
): "keep-local" | "keep-remote" | "keep-both" {
  // 삭제 vs 편집이 엇갈리면 데이터 보존 쪽(둘 다 보관)을 제안.
  if (local.deleted !== remote.deleted) return "keep-both";
  // 그 외에는 더 최신 rev를 제안(사용자가 바꿀 수 있음).
  if (local.rev !== remote.rev) return local.rev > remote.rev ? "keep-local" : "keep-remote";
  return "keep-both";
}

/** 매니페스트 diff 결과: 무엇을 pull/push할지, 어떤 id가 충돌 후보인지(결정 #20). */
export interface ManifestDiff {
  /** 로컬에 가져와야 할 원격 레코드 id. */
  toPull: string[];
  /** 원격에 보내야 할 로컬 레코드 id. */
  toPush: string[];
  /** 사용자 판단이 필요한 충돌 후보 id. */
  conflicts: string[];
}

/**
 * 로컬/원격 매니페스트(+직전 베이스)를 비교해 전송 계획을 만든다(결정 #20).
 * 바뀐 레코드만 주고받으므로 대역폭/6s+ 부담이 작다.
 */
export function diffManifests(
  local: SyncManifest,
  remote: SyncManifest,
  base: SyncManifest = {},
): ManifestDiff {
  const ids = new Set<string>([
    ...Object.keys(local),
    ...Object.keys(remote),
  ]);
  const diff: ManifestDiff = { toPull: [], toPush: [], conflicts: [] };

  for (const id of ids) {
    const decision = decideMerge(local[id], remote[id], base[id]);
    switch (decision) {
      case "use-local":
        diff.toPush.push(id);
        break;
      case "use-remote":
        diff.toPull.push(id);
        break;
      case "conflict":
        diff.conflicts.push(id);
        break;
      case "equal":
        break;
    }
  }
  return diff;
}

/**
 * 전역 설정의 필드 단위 병합(결정 #20). 각 필드의 fieldUpdatedAt 타임스탬프를
 * 비교해 더 최근에 바뀐 쪽 값을 채택한다. 충돌 없이 자동 병합되는 유일한 종류.
 *
 * @returns 병합된 설정 + 갱신된 fieldUpdatedAt.
 */
export function mergeSettingsFields<T extends Record<string, unknown>>(
  local: T,
  remote: T,
  localTimes: Record<string, number> = {},
  remoteTimes: Record<string, number> = {},
): { merged: T; fieldUpdatedAt: Record<string, number> } {
  const merged = { ...local } as Record<string, unknown>;
  const times: Record<string, number> = { ...localTimes };
  const keys = new Set([...Object.keys(local), ...Object.keys(remote)]);

  for (const key of keys) {
    const lt = localTimes[key] ?? 0;
    const rt = remoteTimes[key] ?? 0;
    if (rt > lt) {
      merged[key] = remote[key];
      times[key] = rt;
    } else {
      // 로컬 우선(동률 포함). 로컬 시각 유지.
      if (key in local) merged[key] = local[key];
      times[key] = lt;
    }
  }

  return { merged: merged as T, fieldUpdatedAt: times };
}
